import type { RouteRecordRaw } from 'vue-router'
import { UserRole } from '@/types/auth'
import ChattersView from '@/views/ChattersView.vue'
import WebinarsView from '@/views/WebinarsView.vue'
import PromptsView from '@/views/PromptsView.vue'
import PromptView from '@/views/PromptView.vue'
import FreeContentsView from '@/views/FreeContentsView.vue'
import BroadcastView from '@/views/BroadcastView.vue'
import ResendTestView from '@/views/ResendTestView.vue'
import LoginView from '@/views/LoginView.vue'
import RegisterView from '@/views/RegisterView.vue'

export interface RouteConfig extends Omit<RouteRecordRaw, 'meta' | 'children'> {
  meta: {
    roles?: UserRole[]
    icon?: string
    title?: string
  }
  children?: RouteConfig[]
}

export const routes: RouteConfig[] = [
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {},
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: { roles: [UserRole.ADMIN] },
  },
  {
    path: '/chatters',
    name: 'chatters',
    component: ChattersView,
    meta: {
      roles: [UserRole.ADMIN, UserRole.CURATORS_LEAD, UserRole.CURATOR],
      icon: '👥',
      title: 'Чаттеры',
    },
  },
  {
    path: '/webinars',
    name: 'webinars',
    component: WebinarsView,
    meta: {
      roles: [UserRole.ADMIN, UserRole.CURATORS_LEAD],
      icon: '🎥',
      title: 'Вебинары',
    },
  },
  {
    path: '/prompts',
    name: 'prompts',
    component: PromptsView,
    meta: {
      roles: [UserRole.ADMIN],
      icon: '📝',
      title: 'Промпты',
    },
    children: [
      {
        path: ':status',
        name: 'prompt',
        component: PromptView,
        meta: {
          roles: [UserRole.ADMIN],
          title: 'Промпт',
        },
      },
    ],
  },
  {
    path: '/free-content',
    name: 'free-content',
    component: FreeContentsView,
    meta: {
      roles: [UserRole.ADMIN],
      icon: '📚',
      title: 'Контент',
    },
  },
  {
    path: '/broadcast',
    name: 'broadcast',
    component: BroadcastView,
    meta: {
      roles: [UserRole.ADMIN],
      icon: '📨',
      title: 'Рассылки',
    },
  },
  {
    path: '/resend-test',
    name: 'resend-test',
    component: ResendTestView,
    meta: {
      roles: [UserRole.ADMIN],
      icon: '🔄',
      title: 'Resend Test',
    },
  },
  {
    path: '/register',
    name: 'register',
    component: RegisterView,
    meta: {
      roles: [UserRole.ADMIN, UserRole.CURATORS_LEAD],
      icon: '⚙️',
      title: 'Пользователи',
    },
  },
]

interface NavigationGroup {
  icon: string
  title: string
  routeNames: string[]
}

const navigationGroups: NavigationGroup[] = [
  {
    icon: '🤖',
    title: 'Inst бот',
    routeNames: [
      'models',
      'publishers',
      'inst-settings-groups',
      'inst-accounts',
      'inst-descriptions',
      'inst-carousels',
    ],
  },
]

// Функция для получения навигационных элементов из конфигурации маршрутов
export function getNavigationItems() {
  const visibleRoutes = routes.filter((route) => route.meta.icon && route.meta.title)
  const items: ReturnType<typeof buildItem>[] = []
  const insertedGroups = new Set<string>()

  for (const route of visibleRoutes) {
    const group = navigationGroups.find((g) => g.routeNames.includes(route.name as string))
    if (group) {
      if (insertedGroups.has(group.title)) continue
      insertedGroups.add(group.title)
      const childRoutes = group.routeNames
        .map((name) => visibleRoutes.find((r) => r.name === name))
        .filter((r): r is RouteConfig => Boolean(r))
      items.push({
        name: group.title,
        path: childRoutes[0]?.path ?? '',
        icon: group.icon,
        roles: Array.from(new Set(childRoutes.flatMap((r) => r.meta.roles ?? []))),
        children: childRoutes.map((r) => ({
          name: r.meta.title!,
          path: r.path,
          icon: r.meta.icon,
          roles: r.meta.roles,
        })),
      })
    } else {
      items.push(buildItem(route))
    }
  }

  return items
}

function buildItem(route: RouteConfig) {
  return {
    name: route.meta.title!,
    path: route.path,
    icon: route.meta.icon!,
    roles: route.meta.roles,
    children: undefined as
      | { name: string; path: string; icon?: string; roles?: UserRole[] }[]
      | undefined,
  }
}
